import React from "react";

import moment from "moment";
import {Card, Row, Col, Divider, Pagination, Spin, Select} from "antd";
import FontAwesome from "react-fontawesome";
import DualLineAxis from "components/stateless/dualLinesChart";
import Table from "components/stateless/antdTable";

import {Loading, showNotification} from "src/components/stateless";
import {fetchPaginatedEarnings, fetchMonthlyStats, fetchAvailableYears} from "src/actions/earnings";

const {Option} = Select;

class TransactionsList extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      selectedYear: moment().year(),
      availableYears: [],
      // Stats (server-side)
      personalStats: [],
      fromMyChildsStats: [],
      myGlobalStats: [],
      generalStats: [],
      a2vStats: [],
      statsLoading: true,
      // Table (paginated)
      tableItems: [],
      tableTotal: 0,
      tablePage: 1,
      tablePageSize: 25,
      tableLoading: true,
    };
  }

  componentDidMount() {
    this.loadAvailableYears();
    this.loadStats();
    this.loadTablePage(1);
  }

  loadAvailableYears = async () => {
    try {
      const years = await fetchAvailableYears(this.props.client);
      this.setState({availableYears: years.length ? years : [moment().year()]});
    } catch (e) {
      this.setState({availableYears: [moment().year()]});
    }
  };

  onYearChange = (year) => {
    this.setState({
      selectedYear: year,
      statsLoading: true,
      tableLoading: true,
      personalStats: [],
      fromMyChildsStats: [],
      myGlobalStats: [],
      generalStats: [],
      a2vStats: [],
    }, () => {
      this.loadAll();
    });
  };

  loadAll = async () => {
    const {user} = this.props;
    const {selectedYear, tablePageSize} = this.state;
    try {
      const [stats, tableResult] = await Promise.all([
        fetchMonthlyStats(this.props.client, {
          year: selectedYear,
          userId: user.id,
          needTva: user.needTva,
        }),
        fetchPaginatedEarnings(this.props.client, {
          limit: tablePageSize,
          offset: 0,
          year: selectedYear,
        }),
      ]);
      this.setState({
        personalStats: stats.personal || [],
        fromMyChildsStats: stats.fromMyChilds || [],
        myGlobalStats: stats.myGlobal || [],
        generalStats: stats.general || [],
        a2vStats: stats.a2v || [],
        statsLoading: false,
        tableItems: tableResult.items || [],
        tableTotal: tableResult.total || 0,
        tablePage: 1,
        tableLoading: false,
      });
    } catch (err) {
      this.setState({statsLoading: false, tableLoading: false});
      showNotification("error", "Erreur", "Impossible de charger les données");
    }
  };

  loadStats = async () => {
    try {
      const {user} = this.props;
      const {selectedYear} = this.state;
      const stats = await fetchMonthlyStats(this.props.client, {
        year: selectedYear,
        userId: user.id,
        needTva: user.needTva,
      });
      this.setState({
        personalStats: stats.personal || [],
        fromMyChildsStats: stats.fromMyChilds || [],
        myGlobalStats: stats.myGlobal || [],
        generalStats: stats.general || [],
        a2vStats: stats.a2v || [],
        statsLoading: false,
      });
    } catch (err) {
      this.setState({statsLoading: false});
      showNotification("error", "Erreur", "Impossible de charger les statistiques");
    }
  };

  loadTablePage = async (page) => {
    this.setState({tableLoading: true});
    try {
      const {tablePageSize, selectedYear} = this.state;
      const offset = (page - 1) * tablePageSize;
      const result = await fetchPaginatedEarnings(this.props.client, {
        limit: tablePageSize,
        offset,
        year: selectedYear,
      });
      this.setState({
        tableItems: result.items || [],
        tableTotal: result.total || 0,
        tablePage: page,
        tableLoading: false,
      });
    } catch (err) {
      this.setState({tableLoading: false});
    }
  };

  renderRow = curr => ({
    ...curr,
    key: curr.id,
    tva: curr.user.needTva ? curr.vatAmount + " €" : 0,
    nettyId: curr.transaction.nettyId,
    user: `${curr.from.firstname} ${curr.from.lastname}`,
    userId: curr.user.id,
    from: `${curr.from.firstname} ${curr.from.lastname}`,
    to: `${curr.user.firstname} ${curr.user.lastname}`,
    date: moment(curr.createdAt).format("DD/MM/YYYY HH:mm:ss"),
    amount: curr.amount + " €",
    profit: curr.user.needTva
      ? `${parseFloat(curr.amount - curr.vatAmount).toFixed(2)} €`
      : `${curr.amount} €`,
    commissionAmount: parseFloat(curr.transaction.commissionAmount).toFixed(2) + " €",
  });

  render() {
    const {user} = this.props;
    const {
      selectedYear, availableYears,
      personalStats, fromMyChildsStats, myGlobalStats, generalStats, a2vStats,
      statsLoading, tableItems, tableTotal, tablePage, tablePageSize, tableLoading,
    } = this.state;

    if (statsLoading && personalStats.length === 0) return <Loading />;

    const subColumns = [
      {dataIndex: "nettyId", title: "Id du bien netty"},
      {
        dataIndex: "date", title: "Date",
        sorter: (a, b) => moment(a.date, "DD/MM/YYYY HH:mm:ss").unix() - moment(b.date, "DD/MM/YYYY HH:mm:ss").unix(),
        defaultSortOrder: "descend",
      },
      {dataIndex: "from", title: "De"},
      {dataIndex: "to", title: "Bénéficiaire"},
      {dataIndex: "type", title: "Type"},
      {dataIndex: "profit", title: "Honoraires HT"},
    ];

    // Filter table items by role
    let tableFiltered = tableItems;
    if (user.role.name !== "Admin") {
      tableFiltered = tableItems.filter(t =>
        t.user.id === user.id ||
        (user.childrens && user.childrens.length && user.childrens.find(u => u.id === t.user.id))
      );
    }
    const list = tableFiltered.map(t => this.renderRow(t));

    return (
      <div>
        <Row>
          <Col span={24}>
            {/* Year selector */}
            <div style={{display: "flex", alignItems: "center", marginBottom: 16}}>
              <span style={{marginRight: 8, fontWeight: 600}}>Année :</span>
              <Select
                value={selectedYear}
                onChange={this.onYearChange}
                style={{width: 100}}
              >
                {availableYears.map(y => (
                  <Option key={y} value={y}>{y}</Option>
                ))}
              </Select>
            </div>

            {user.role.name === "Admin" && (
              <div>
                <h5 style={{background: "black", color: "white", padding: 10, borderRadius: 4}}>
                  Statistiques globales {selectedYear}
                </h5>
                <Row gutter={16} style={{marginTop: 10}}>
                  <Col span={12}>
                    <Card title={<h5><FontAwesome name="chart-line" style={{color: "#1890ff"}} /> CA global Société (HT)</h5>}>
                      <DualLineAxis data={generalStats} />
                    </Card>
                  </Col>
                  <Col span={12}>
                    <Card title={<h5><FontAwesome name="chart-line" style={{color: "#1890ff"}} /> CA mensuel A2V (HT)</h5>}>
                      <DualLineAxis data={a2vStats} />
                    </Card>
                  </Col>
                </Row>
              </div>
            )}
            <h5 style={{background: "black", color: "white", padding: 10, marginTop: user.role.name === "Admin" ? 20 : 0, borderRadius: 4}}>
              Statistiques Personnelles {selectedYear}
            </h5>
            <Row gutter={16} style={{marginTop: 20}}>
              <Col span={8}>
                <Card title={<h5><FontAwesome name="chart-line" style={{color: "#1890ff"}} /> CA Production personnelle (HT)</h5>}>
                  <DualLineAxis data={personalStats} />
                </Card>
              </Col>
              <Col span={8}>
                <Card title={<h5><FontAwesome name="chart-pie" style={{color: "#1890ff"}} /> CA rémunération équipe (HT)</h5>}>
                  <DualLineAxis data={fromMyChildsStats} />
                </Card>
              </Col>
              <Col span={8}>
                <Card title={<h5><FontAwesome name="euro-sign" style={{color: "#1890ff"}} /> Total Chiffre d'affaire personnel (HT)</h5>}>
                  <DualLineAxis data={myGlobalStats} />
                </Card>
              </Col>
            </Row>
            <Divider />
            <Row gutter={16} style={{marginTop: 20}}>
              <Col span={24}>
                <Card title={<h5><FontAwesome name="chart-line" style={{color: "#1890ff"}} /> Table des répartitions</h5>}>
                  <Spin spinning={tableLoading}>
                    <Table
                      data={list}
                      columns={subColumns}
                      {...this.props}
                    />
                  </Spin>
                  <div style={{display: "flex", justifyContent: "flex-end", padding: "12px 0"}}>
                    <Pagination
                      current={tablePage}
                      total={tableTotal}
                      pageSize={tablePageSize}
                      onChange={(p) => this.loadTablePage(p)}
                      onShowSizeChange={(_, size) => this.setState({tablePageSize: size}, () => this.loadTablePage(1))}
                      showSizeChanger
                      showTotal={(total) => `${total} gains`}
                      pageSizeOptions={["10", "25", "50", "100"]}
                    />
                  </div>
                </Card>
              </Col>
            </Row>
          </Col>
        </Row>
      </div>
    );
  }
}

export default TransactionsList;
